import { apiCall, isBackendConfigured } from './api-call'
import { mergeProfilePatch, readProfile, writeProfile } from './local-store'
import {
  QuickPassProfile,
  CreateOrUpdateQuickPassProfileInput,
} from '../_types/quickpass-profile'

const warnFallback = (operation: string, error: unknown): void => {
  console.warn(
    `[quickpass-profile] backend ${operation} failed, falling back to localStorage —`,
    error,
  )
}

/**
 * Fetches the renter's profile by user id. Returns null when no profile
 * exists. Falls back to local storage when backend is unconfigured or
 * unreachable.
 *
 * @param {string} quickPassUserId
 * @returns {Promise<QuickPassProfile | null>}
 */
const getQuickPassProfileByUserId = async (
  quickPassUserId: string,
): Promise<QuickPassProfile | null> => {
  if (isBackendConfigured() === false) {
    return readProfile(quickPassUserId)
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'getprofilebyuserid',
      data: { quickPassUserId },
    })

    if (response.code === 404) {
      return null
    }

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as QuickPassProfile
  } catch (error) {
    warnFallback('getProfileByUserId', error)
    return readProfile(quickPassUserId)
  }
}

/**
 * Idempotent upsert of the profile. Backend first, localStorage on
 * backend failure.
 *
 * @param {CreateOrUpdateQuickPassProfileInput} input
 * @returns {Promise<QuickPassProfile>}
 */
const createOrUpdateQuickPassProfile = async (
  input: CreateOrUpdateQuickPassProfileInput,
): Promise<QuickPassProfile> => {
  if (isBackendConfigured() === true) {
    try {
      const response = await apiCall({
        group: 'quickpass',
        command: 'createorupdateprofile',
        data: input,
      })

      if (response.code !== 200) {
        throw new Error(response.message)
      }

      return JSON.parse(response.data) as QuickPassProfile
    } catch (error) {
      warnFallback('createOrUpdateProfile', error)
    }
  }

  const previous = readProfile(input.quickPassUserId)
  const merged = mergeProfilePatch(input.quickPassUserId, input.patch, previous)
  writeProfile(merged)
  return merged
}

/**
 * Patches an existing profile.
 *
 * @param {string} quickPassUserId
 * @param {Partial<QuickPassProfile>} updates
 * @returns {Promise<QuickPassProfile>}
 */
const updateQuickPassProfile = async (
  quickPassUserId: string,
  updates: Partial<QuickPassProfile>,
): Promise<QuickPassProfile> => {
  if (isBackendConfigured() === true) {
    try {
      const response = await apiCall({
        group: 'quickpass',
        command: 'updateprofile',
        data: { quickPassUserId, updates },
      })

      if (response.code !== 200) {
        throw new Error(response.message)
      }

      return JSON.parse(response.data) as QuickPassProfile
    } catch (error) {
      warnFallback('updateProfile', error)
    }
  }

  const previous = readProfile(quickPassUserId)

  if (previous === null) {
    throw new Error('QuickPass profile not found')
  }

  const merged = mergeProfilePatch(quickPassUserId, updates, previous)
  writeProfile(merged)
  return merged
}

/**
 * Adds a listing id to the renter's saved-listings set.
 *
 * @param {string} quickPassUserId
 * @param {string} listingId
 * @param {string | undefined} savedFromHost - the host site the renter was browsing when they saved (embedded SDK only), recorded so the saved-listings view can be scoped to that site
 * @returns {Promise<QuickPassProfile>}
 */
const saveListing = async (
  quickPassUserId: string,
  listingId: string,
  savedFromHost: string | undefined = undefined,
): Promise<QuickPassProfile> => {
  if (isBackendConfigured() === true) {
    try {
      const response = await apiCall({
        group: 'quickpass',
        command: 'savelisting',
        data: { quickPassUserId, listingId, savedFromHost },
      })

      if (response.code !== 200) {
        throw new Error(response.message)
      }

      return JSON.parse(response.data) as QuickPassProfile
    } catch (error) {
      warnFallback('saveListing', error)
    }
  }

  const previous = readProfile(quickPassUserId)
  const baseProfile =
    previous ??
    mergeProfilePatch(quickPassUserId, { savedListingIds: [] }, null)

  if (baseProfile.savedListingIds.includes(listingId)) {
    return baseProfile
  }

  const resolvedHost =
    typeof savedFromHost === 'string' && savedFromHost !== ''
      ? savedFromHost
      : null

  const patch: Partial<QuickPassProfile> = {
    savedListingIds: [...baseProfile.savedListingIds, listingId],
  }

  if (resolvedHost !== null) {
    patch.savedListingHosts = {
      ...(baseProfile.savedListingHosts ?? {}),
      [listingId]: resolvedHost,
    }
  }

  const merged = mergeProfilePatch(quickPassUserId, patch, baseProfile)
  writeProfile(merged)
  return merged
}

/**
 * Removes a listing id from the renter's saved-listings set.
 *
 * @param {string} quickPassUserId
 * @param {string} listingId
 * @returns {Promise<QuickPassProfile>}
 */
const unsaveListing = async (
  quickPassUserId: string,
  listingId: string,
): Promise<QuickPassProfile> => {
  if (isBackendConfigured() === true) {
    try {
      const response = await apiCall({
        group: 'quickpass',
        command: 'unsavelisting',
        data: { quickPassUserId, listingId },
      })

      if (response.code !== 200) {
        throw new Error(response.message)
      }

      return JSON.parse(response.data) as QuickPassProfile
    } catch (error) {
      warnFallback('unsaveListing', error)
    }
  }

  const previous = readProfile(quickPassUserId)

  if (previous === null) {
    throw new Error('QuickPass profile not found')
  }

  const merged = mergeProfilePatch(
    quickPassUserId,
    {
      savedListingIds: previous.savedListingIds.filter(
        existing => existing !== listingId,
      ),
    },
    previous,
  )
  writeProfile(merged)
  return merged
}

export {
  getQuickPassProfileByUserId,
  createOrUpdateQuickPassProfile,
  updateQuickPassProfile,
  saveListing,
  unsaveListing,
}
